import React from "react";
import { Route, Switch } from "react-router-dom";
import { getPageIndex } from "../../components/FormNav/FormNavUtils";
import { NavItem } from "../../components/FormNav/NavItem";
import FormPages from "../../config/FormPages";
import {
    AdditionalDistributionLayer,
    Beneficiaries,
    Distribution,
    DistributionType,
    EstateItemTypes,
    RevocableTrust
} from "../../model/EstateFlowchartTypes";
import BasicInformationContent from "../../pages/BasicInformation/BasicInformationContent";
import BeneficiariesFirstDeathContent from "../../pages/BeneficiariesAtFirstDeath/BeneficiariesFirstDeathContent";
import AdditionalDistributionsContent from "../../pages/AdditionalDistributions/AdditionalDistributionsContent";
import RemainderBeneficiariesContent from "../../pages/RemainderBeneficiaries/RemainderBeneficiariesContent";

interface RevocableTrustContentProps {
    revocableTrust: RevocableTrust;
    pageConfig: NavItem[];
    updateRevocableTrust: (revTrust: RevocableTrust) => void;
    saveForm: () => Promise<boolean>;
}

const ensureAdditionalDistributions = (
    layer?: AdditionalDistributionLayer
): AdditionalDistributionLayer => ({
    triggerEventType: layer?.triggerEventType ?? "",
    triggerMemberId: layer?.triggerMemberId ?? "",
    triggerAge: layer?.triggerAge ?? null,
    triggerEventText: layer?.triggerEventText ?? "",
    distributions: layer?.distributions ?? []
});

const RevocableTrustContent =
    ({
        revocableTrust,
        pageConfig,
        updateRevocableTrust,
        saveForm
    }: RevocableTrustContentProps): JSX.Element => {

        return (
            <Switch>
                <Route
                    path="/Profile/:id/ClientProfile/EstateFlowchart/:flowchartId/RevocableTrust/:trustId/BasicInformation">
                    <BasicInformationContent
                        basicInformation={revocableTrust}
                        updateEstateItem={(basicInformation: EstateItemTypes) => {
                            updateRevocableTrust(basicInformation as RevocableTrust);
                        }}
                        pageIndex={getPageIndex(pageConfig, FormPages.BASIC_INFORMATION.id)}
                    />
                </Route>
                <Route
                    path="/Profile/:id/ClientProfile/EstateFlowchart/:flowchartId/RevocableTrust/:trustId/BeneficiariesAtFirstDeath">
                    <BeneficiariesFirstDeathContent
                        distributions={revocableTrust?.beneficiaries?.firstDeathDistributions ?? []}
                        updateEstateItem={(firstDeathDistributions: Distribution[]) => {
                            updateRevocableTrust({
                                ...revocableTrust,
                                beneficiaries: {
                                    ...revocableTrust.beneficiaries,
                                    firstDeathDistributions: firstDeathDistributions
                                } as Beneficiaries
                            });
                        }}
                        distributionDropdownOptionTypes={[
                            DistributionType.CREDIT_SHELTER,
                            DistributionType.IN_TRUST,
                            DistributionType.MARITAL,
                            DistributionType.SURVIVOR,
                            DistributionType.CHARITY,
                            DistributionType.OUTRIGHT
                        ]}
                        saveForm={saveForm}
                        pageIndex={getPageIndex(pageConfig, FormPages.BENEFICIARIES_AT_FIRST_DEATH.id)}
                    />
                </Route>
                <Route
                    path="/Profile/:id/ClientProfile/EstateFlowchart/:flowchartId/RevocableTrust/:trustId/RemainderBeneficiaries">
                    <RemainderBeneficiariesContent
                        remainderDistributions={revocableTrust?.beneficiaries?.remainderDistributions ?? []}
                        additionalDistributions={revocableTrust?.beneficiaries?.additionalDistributions}
                        updateEstateItem={(remainderDistributions: Distribution[]) => {
                            updateRevocableTrust({
                                ...revocableTrust,
                                beneficiaries: {
                                    ...revocableTrust.beneficiaries,
                                    remainderDistributions: remainderDistributions
                                } as Beneficiaries
                            })
                        }}
                        updateAdditionalDistributions={(additionalDistributions?: AdditionalDistributionLayer) => {
                            updateRevocableTrust({
                                ...revocableTrust,
                                beneficiaries: {
                                    ...revocableTrust.beneficiaries,
                                    additionalDistributions: additionalDistributions
                                } as Beneficiaries
                            })
                        }}
                        saveForm={saveForm}
                        pageIndex={getPageIndex(pageConfig, FormPages.REMAINDER_BENEFICIARIES.id)}
                    />
                </Route>
                <Route
                    path="/Profile/:id/ClientProfile/EstateFlowchart/:flowchartId/RevocableTrust/:trustId/AdditionalDistributions">
                    <AdditionalDistributionsContent
                        pageIndex={getPageIndex(pageConfig, FormPages.ADDITIONAL_DISTRIBUTIONS.id)}
                        triggerEventData={revocableTrust?.beneficiaries?.additionalDistributions}
                        distributions={
                            revocableTrust?.beneficiaries?.additionalDistributions?.distributions ?? []
                        }
                        updateEstateItem={(additionalDistributionsList: Distribution[]) => {
                            const additionalDistributions = ensureAdditionalDistributions(
                                revocableTrust?.beneficiaries?.additionalDistributions
                            );
                            updateRevocableTrust({
                                ...revocableTrust,
                                beneficiaries: {
                                    ...revocableTrust.beneficiaries,
                                    additionalDistributions: {
                                        ...additionalDistributions,
                                        distributions: additionalDistributionsList
                                    }
                                } as Beneficiaries
                            });
                        }}
                        saveForm={saveForm}
                    />
                </Route>
            </Switch>
        )
    }

export default RevocableTrustContent;